import fscreen from "fscreen";
import { useCallback, useEffect, useMemo, useState } from "react";

import { cn } from "@/utils/core/generalUtils";

type FullScreenHandle = {
  active: boolean;
  enter: () => void;
  exit: () => void | Promise<void>;
};

interface FullScreenProps {
  handle: FullScreenHandle;
  onChange?: (active: boolean, handle: FullScreenHandle) => void;
  children?: React.ReactNode;
  className?: string;
}

export const useFullScreenHandle = (): FullScreenHandle => {
  const [active, setActive] = useState(false);

  const handleFullScreenChange = (): void => {
    setActive(fscreen.fullscreenElement === document.body);
  };

  useEffect(() => {
    fscreen.addEventListener("fullscreenchange", handleFullScreenChange);

    return (): void =>
      fscreen.removeEventListener("fullscreenchange", handleFullScreenChange);
  }, []);

  const requestFullscreen = (): void =>
    fscreen.requestFullscreen(document.body);

  const enter = useCallback(() => {
    if (fscreen.fullscreenElement) {
      return fscreen.exitFullscreen();
    }

    return requestFullscreen();
  }, []);

  const exit = useCallback(() => {
    if (fscreen.fullscreenElement !== document.body) {
      return Promise.resolve();
    }

    return fscreen.exitFullscreen();
  }, []);

  return useMemo(
    () => ({
      active,
      enter,
      exit,
    }),
    [active, enter, exit],
  );
};

export const FullScreen = ({
  handle,
  onChange,
  children,
  className = "",
}: FullScreenProps): JSX.Element => {
  useEffect(() => {
    if (onChange) {
      onChange(handle.active, handle);
    }
  }, [handle, handle.active, onChange]);

  const styles = handle.active
    ? {
        height: "100%",
        width: "100%",
      }
    : {};

  return (
    <div
      className={cn(
        "fullscreen",
        className,
        handle.active ? "fullscreen-enabled" : "",
      )}
      style={styles}
    >
      {children}
    </div>
  );
};
